import {CallableDisposableStack, StackDisposable} from "../lib/disposable";
import {IHierarchy} from "../lib/hierarchy";
import {observable, signal, Signal} from "../lib/signal";
import {MessageActivity} from "./HelperActivites";
import {CancelledError, retry} from "../util/CoreUtils";
import {EL} from "../util/DomUtils";
import {Activity, GoAwayError, IActivity, IActivityHost, MustRecreateError, OnNavigateOptions} from "./Activity";
import {IResolver, RegistryLookupError} from "../core/IResolver";
import {UiContext} from "../util/UiContext";
import {Resolver} from "../core/Resolver";

export function isSamePageUrl(url1: URL | undefined, url2: URL | undefined): boolean {
    if (url1 === url2 || (!url1 && !url2)) return true;
    if (!url1 || !url2) return false;
    return url1.protocol === url2.protocol && url1.host === url2.host && url1.username === url2.username && url1.password === url2.password && url1.pathname === url2.pathname && url1.search === url2.search;
}

const __initUICalled = Symbol('__initUICalled');

class ActivityLoadingException extends Error {
    constructor(public message: string, public ex: Error, public retryUrl: URL, public icon?: string) {
        super(message, {cause: ex});
    }
}

@StackDisposable.Mixin
export class ActivityHolder implements StackDisposable, IHierarchy {
    declare [Symbol.dispose]: CallableDisposableStack;
    declare dtor: DisposableStack;

    public get contentUrl(): URL | null {
        return this._contentUrl;
    }
    private _contentUrl: URL | null;

    public get activityUrl(): URL | null {
        return this._activityUrl;
    }
    private _activityUrl: URL | null;

    public get url(): URL | null {
        return this._url;
    }
    private _url: URL | null;

    public static readonly instances = new Set<ActivityHolder>();

    @observable
    public activity: IActivity;
    declare activity$: Signal<(old: IActivity | undefined, newval: IActivity | undefined) => void>;

    @observable
    public isPinned = false;
    readonly navigated = signal<(url: URL) => void>();
    readonly beforeNavigate = signal<(newUrl: URL, previousUrl: URL) => void | Promise<void>>();

    private raceCtr = 0;

    get children(): IHierarchy[] { return this.activity ? [this.activity] : []; }

    constructor(public readonly parent: IActivityHost) {
        this.dtor.defer(() => this.activity?.[Symbol.dispose]());
        this.dtor.defer(() => ActivityHolder.instances.delete(this));
        ActivityHolder.instances.add(this);
    }

    async onBeforeNavigate(url: URL, options: OnNavigateOptions): Promise<void> {
        if (typeof options.pinned === 'boolean') this.isPinned = options.pinned;
        else if (this.isPinned && !isSamePageUrl(url, options.previousUrl)) throw new GoAwayError("Navigation of pinned content prevented.");
        await this.beforeNavigate.asyncCollect(url, options.previousUrl);
    }
    async onNavigate(url: URL, options: OnNavigateOptions) {
        this._url = url;
        const ctr = ++this.raceCtr;

        let loadIndicatorVisible = false;
        if (!this.activity) {
            if (this.raceCtr !== ctr) return;
            await this.setActivity(new MessageActivity(`loading ...`, `${url}`, undefined, EL('load-indicator', {center: ''})));
            loadIndicatorVisible = true;
        }

        try {
            let resolverResult;
            try {
                resolverResult = await retry(5, 1000, async () =>
                    await UiContext.instance.resolver.resolve(url, options.cancel), RegistryLookupError, options.cancel);
            } catch(ex) {
                throw new ActivityLoadingException(`unable to resolve ${url}`, ex, url, '⚡');
            }

            if (isActivity(resolverResult)) {
                if (this.raceCtr !== ctr) return;
                await this.setActivity(resolverResult);
                await this.activity.onBeforeNavigate(null, options);
                await this.activity.onNavigate(null, options);
                this._activityUrl = url;
                this._contentUrl = null;
            } else if (typeof resolverResult === 'object' && resolverResult && 'content' in resolverResult && 'activity' in resolverResult) {
                let createActivity = null;
                let theNewActivity = null;
                if (resolverResult.activity instanceof URL) {
                    createActivity = async () => {
                        if (!loadIndicatorVisible) {
                            if (this.raceCtr !== ctr) return;
                            await this.setActivity(new MessageActivity(`loading ...`, `${url}`, undefined, EL('load-indicator', {center: ''})));
                            loadIndicatorVisible = true;
                        }

                        let activity : IActivity;
                        try {
                            activity = await retry(5, 1000, async () =>
                                await UiContext.instance.resolver.resolve(resolverResult.activity, options.cancel), RegistryLookupError, options.cancel);
                        } catch (ex) {
                            throw new ActivityLoadingException(`unable to create ${resolverResult.activity}`, ex, url, '⚡');
                        }
                        if (!isActivity(activity))
                            throw new ActivityLoadingException(`not a valid activity ref`, resolverResult.activity, url, '🚫');
                        this._activityUrl = resolverResult.activity;
                        return activity;
                    };
                    if (this._activityUrl?.toString() !== resolverResult.activity.toString() || options.refresh) {
                        theNewActivity = await createActivity();
                        createActivity = null;
                    }
                } else if (isActivity(resolverResult.activity)) {
                    theNewActivity = resolverResult.activity;
                    this._activityUrl = null;
                } else {
                    throw new ActivityLoadingException(`not a valid activity ref`, resolverResult.activity, url, '🚫');
                }

                let _content : any, _contentUrl : URL;
                if (resolverResult.content instanceof URL) {
                    try {
                        _contentUrl = resolverResult.content;
                        _content = await retry(3, 1000, async () =>
                            await UiContext.instance.resolver.resolve(resolverResult.content, options.cancel), RegistryLookupError, options.cancel);
                    } catch (ex) {
                        throw new ActivityLoadingException(`unable to load ${resolverResult.content}`, ex, url, '⚡');
                    }
                } else {
                    _contentUrl = null;
                    _content = resolverResult.content;
                }
                options.contentUrl = _contentUrl;

                if (this.raceCtr !== ctr) return;
                if (theNewActivity !== null) await this.setActivity(theNewActivity);

                do {
                    try {
                        try {
                            await this.activity.onBeforeNavigate(_content, options);
                        } catch (e) {
                            if (e instanceof CancelledError) return;
                            throw e;
                        }

                        this._contentUrl = _contentUrl;
                        await this.activity.onNavigate(_content, options);
                    } catch (ex) {
                        if (ex instanceof MustRecreateError && createActivity) {
                            if (this.raceCtr !== ctr) return;
                            await this.setActivity(await createActivity());
                            createActivity = null;
                            continue;
                        }
                        throw new ActivityLoadingException(`unable to display ${_contentUrl || ''}`, ex, url, '⚡');
                    }
                    break;
                } while (true);
            } else {
                throw new ActivityLoadingException(`i don't know what to do with that`, resolverResult, url, '🚫');
            }
        } catch (ex) {
            if (ex instanceof ActivityLoadingException) {
                await this.setError(ex.message, ex.ex, ex.retryUrl, ex.icon);
            } else {
                await this.setError(`unhandled exception`, ex, url, '⚠️');
            }
            return;
        }

        this.navigated(url);
    }
    async setActivity(activity: Activity) {
        if (this.activity === activity) return;

        if (this.activity) {
            const old = this.activity;
            await old.onBeforeUnload();

            this.activity = null;
            old[Symbol.dispose]();
        }

        this.activity = activity;
        if (!(__initUICalled in this.activity)) {
            await this.activity.initUI();
            this.activity[__initUICalled] = true;
        }
        this.activity.attachToHost(this.parent);
    }
    async setError(message: string, ex: Error, retryUrl: URL, icon?: string) {
        console.warn(message, ex);
        await this.setActivity(new MessageActivity(
            `${message}`,
            `${ex}`,
            EL('button', {onclick: () => this.parent.navigate(retryUrl, {})}, 'try again'),
            icon,
        ));
        this._activityUrl = null;
        this._contentUrl = null;
    }

    // TODO: maybe this should somehow be called automatically as soon as the activity is added elsewhere...?
    releaseActivity(): IActivity {
        this.activity.attachToHost(null);
        this.activity = null;
        return this.activity;
    }
}

function isActivity(an: object): boolean {
    return typeof an === 'object' && an && 'attachToHost' in an && 'onNavigate' in an && 'initUI' in an;
}

export function registerActivityResolvers(resolver: Resolver) {
    resolver.protocols['activity:'] = new ActivityResolver();
    resolver.protocols['viewspec:'] = {
        async resolve(url: URL, cancel?: AbortSignal) {
            return {
                activity: new URL(url.searchParams.get('activity')),
                content: new URL(url.searchParams.get('content'))
            };
        }
    };
    resolver.protocols['a:'] = {
        async resolve(url: URL, cancel?: AbortSignal) {
            const [_, at, ...rest] = url.toString().split(':');
            return {
                activity: new URL('activity:' + at),
                content: new URL(rest.join(':'))
            };
        }
    };
    resolver.protocols['w:'] = {
        async resolve(url: URL, cancel?: AbortSignal) {
            const [_, wt, ...rest] = url.toString().split(':');
            return {
                activity: new URL('activity:legacyDomWindowWrapper?inner=' + wt),
                content: new URL('url:' + wt + ':' + rest.join(':'))
            };
        }
    };
    resolver.protocols['memo:'] = {
        async resolve(url: URL, cancel?: AbortSignal) {
            const [_, ...rest] = url.toString().split(':');
            return {
                activity: new URL('activity:memo'),
                content: new URL('doc:' + rest.join(':'))
            };
        }
    };
    resolver.protocols['url:'] = {
        async resolve(url: URL, cancel?: AbortSignal) {
            const [_, ...rest] = url.toString().split(':');
            return new URL(rest.join(':'));
        }
    };
    resolver.protocols['about:'] = {
        async resolve(url: URL, cancel?: AbortSignal) {
            if (url.toString() === 'about:blank')
                return new MessageActivity("This space intentionally left blank.");
            else
                throw new Error("Invalid URL");
        }
    };
}

export class ActivityResolver implements IResolver {
    async resolve(url: URL, cancel?: AbortSignal) {
        const typeFromUrl = url.pathname.toLowerCase();
        for(const type of Activity.activityTypes.flatCollect()) {
            if (type.name.toLowerCase() == typeFromUrl + 'activity' || type.protocol?.toLowerCase() === typeFromUrl) {
                const ctor = ('getConstructor' in type ? await type.getConstructor() : type);
                return new ctor({ activityUrl: url });
            }
        }
        throw new RegistryLookupError(`activity type`, typeFromUrl);
    }
}
